






























































































































































































































































































































































































































import {Component, Mixins} from 'vue-mixin-decorator';
import {Vue, Watch} from 'vue-property-decorator';
import {getModule} from 'vuex-module-decorators';
import {Inject} from 'inversify-props';
import Axios, {CancelTokenSource} from 'axios';

import {Notify} from '@/utils/Notify';

import {NetworkingStatus, NetworkingStatusColor, NetworkingStatusLabel} from '@/model/network/Networking';
import {LoraForwarderStateColor} from '@/model/lora/LoraForwarderState';
import {ReleaseFileType, ReleaseInfo} from '@/model/system/update/Release';
import {UpgradeState} from '@/model/system/update/UpgradeState';

import {IApiService} from '@/services/IApi.service';
import {ISystemOsService} from '@/services/system/IOs.service';
import {ISystemUpgradeService} from '@/services/system/IUpgrade.service';

import LoraForwardersStore from '@/store/lora/LoraForwarders.store';
import NetworkNetworkingStore from '@/store/network/NetworkNetworking.store';
import SystemUpdateStore from '@/store/system/SystemUpdate.store';
import SystemUpgradeStore from '@/store/system/SystemUpgrade.store';
import SystemInfoStore from '@/store/system/SystemInfo.store';

import {BaseComponent} from '@/mixins/BaseComponent';
import SystemUpdateChecker from '@/components/System/SystemUpdateChecker.vue';
import {Time} from '@/utils/Time';

enum UpgradeStep {
  Select = 1,
  Apply = 2,
  Check = 3,
}

@Component({
  components: {SystemUpdateChecker},
})
export default class SystemUpgrader extends Mixins<BaseComponent>(BaseComponent) {
  @Inject(Symbol.for('IApiService'))
  private apiService!: IApiService;

  @Inject(Symbol.for('ISystemOsService'))
  private systemOsService!: ISystemOsService;

  @Inject(Symbol.for('ISystemUpgradeService'))
  private systemUpgradeService!: ISystemUpgradeService;

  private systemInfoStore: SystemInfoStore = getModule(SystemInfoStore, this.$store);

  private systemUpgradeStore: SystemUpgradeStore = getModule(SystemUpgradeStore, this.$store);

  private systemUpdateStore: SystemUpdateStore = getModule(SystemUpdateStore, this.$store);

  private networkingStore: NetworkNetworkingStore = getModule(NetworkNetworkingStore, this.$store);

  private loraForwardersStore: LoraForwardersStore = getModule(LoraForwardersStore, this.$store);

  private UpgradeStep = UpgradeStep;
  private UpgradeState = UpgradeState;

  private NetworkingStatus = NetworkingStatus;

  private artifactFile: File | null = null;

  private updateConfirmationModal: boolean = false;

  /*  Download / upload */
  private downloadProgression: number = 0;
  private downloadCancelToken: CancelTokenSource = Axios.CancelToken.source();

  private uploadProgression: number = 0;
  private uploadCancelToken: CancelTokenSource = Axios.CancelToken.source();

  private uploadTimeout: number = 5 * 60 * 1000;
  private uploadTimeoutSlow: number = 30 * 60 * 1000;
  private slowConnection: boolean = false;

  /* Update */
  private updateTimeout: number = 5 * 60;
  private updateTimer: number = 0;
  private updateTimerInterval: any | null = null;

  private shutdownTimeout: number = 30;
  private shutdownCheckPeriod: number = 2;
  private shutdownCheckTimeout: number = 10;

  private rebootTimeout: number = 4 * 60;
  private rebootCheckPeriod: number = 5;
  private rebootCheckTimeout: number = 10;

  /* Checks */
  private rollbackConfirmationModal: boolean = false;

  private checksStatusRefreshing: boolean = false;
  private checksRefreshPeriod: number = 5;
  private checksRefreshInterval: any | null = null;

  private NetworkingStatusColor = NetworkingStatusColor;
  private NetworkingStatusLabel = NetworkingStatusLabel;

  private LoraForwarderStateColor = LoraForwarderStateColor;

  private async mounted() {
    await this.refresh();
  }

  private async beforeDestroy() {
    if (this.updateTimerInterval) {
      clearInterval(this.updateTimerInterval);
      this.updateTimerInterval = null;
    }

    if (this.checksRefreshInterval) {
      clearInterval(this.checksRefreshInterval);
      this.checksRefreshInterval = null;
    }
  }

  private async refresh() {
    this.setLoading();
    try {
      await this.systemUpgradeStore.fetchState();
      await this.getNetworkStatus();

      if (this.upgradeStep === UpgradeStep.Check) {
        await this.performChecks();
      }
    } catch (error) {
      Notify.Error(this.logger, 'Could not load upgrade information', error);
    } finally {
      this.setLoaded();
    }
  }

  private async getNetworkStatus() {
    try {
      await this.networkingStore.fetchStatus();
    } catch (error) {
      Notify.Error(this.logger, `Could not get gateway network status`, error);
    }
  }

  private reset() {
    this.systemUpgradeStore.setState(UpgradeState.Idle);
  }

  get upgradeStep(): UpgradeStep {
    switch (this.systemUpgradeStore.UpgradeState) {
      case UpgradeState.Idle:
      case UpgradeState.Uploading:
      case UpgradeState.UploadFailed:
      default:
        return UpgradeStep.Select;

      case UpgradeState.Downloaded:
      case UpgradeState.Applying:
      case UpgradeState.Pending:
      case UpgradeState.Stopping:
      case UpgradeState.Starting:
      case UpgradeState.ApplyFailed:
      case UpgradeState.RebootTimedOut:
        return UpgradeStep.Apply;

      case UpgradeState.Checking:
      case UpgradeState.Committing:
      case UpgradeState.Completed:
      case UpgradeState.CommitFailed:
        return UpgradeStep.Check;
    }
  }

  @Watch('systemUpgradeStore.UpgradeState')
  private onUpgradeStateUpdated() {

    switch (this.upgradeStep) {
      case UpgradeStep.Select:
        (this.$refs.updateChecker as Vue & { checkUpdateAvailable: () => void }).checkUpdateAvailable();
        break;
    }

    if (this.systemUpgradeStore.UpgradeState !== UpgradeState.Checking) {
      if (this.checksRefreshInterval) {
        clearInterval(this.checksRefreshInterval);
        this.checksRefreshInterval = null;
      }
    }
  }

  private async download(release: ReleaseInfo) {
    if (this.networkingStore.Status === NetworkingStatus.ConnectedGlobal) {
      await this.remoteDownload(release);
    } else {
      this.updateConfirmationModal = true;
    }
  }

  private async remoteDownload(release: ReleaseInfo) {

    this.logger.debug(`Starting remote system update`, release);

    this.systemUpgradeStore.setState(UpgradeState.Downloading);
    try {
      await this.systemUpgradeService.download(release.path);

      const refreshStateHandle = setInterval(async () => {
        await this.systemUpgradeStore.checkDownloaded();
        if (this.systemUpgradeStore.UpgradeState !== UpgradeState.Downloading) {
          clearInterval(refreshStateHandle);
        }
      }, 1000);

    } catch (error) {
      Notify.Error(this.logger, `Could not start the update`, error);
      this.systemUpgradeStore.setState(UpgradeState.DownloadFailed);
    }
  }

  private async downloadArtifact(release: ReleaseInfo) {

    this.updateConfirmationModal = false;
    this.systemUpgradeStore.setState(UpgradeState.Downloading);

    try {
      if (!this.systemUpdateStore.ReleaseInfo) {
        throw new Error(`No release provided`);
      }

      await this.systemInfoStore.fetchOsInfo();

      if (!this.systemInfoStore.OsInfo) {
        throw new Error(`Could not get OS information`);
      }

      const machine = this.systemInfoStore.OsInfo.Machine;

      const artifact = this.systemUpdateStore.ReleaseInfo.files.find((f) => f.type === ReleaseFileType.Artifact && f.machines.includes(machine));
      if (!artifact) {
        throw new Error(`Could not find an artifact for '${this.systemInfoStore.OsInfo.Machine}'`);
      }

      const artifactUrl = `${release.path}/${artifact.url}`;

      try {
        this.downloadCancelToken = Axios.CancelToken.source();
        this.downloadProgression = 0;

        const client = Axios.create();
        const artifactFileResponse = await client.get(artifactUrl, {
          responseType: 'blob',
          onDownloadProgress: (progressEvent: any) => {
            this.downloadProgression = progressEvent.loaded / artifact.size * 100;
          },
          cancelToken: this.downloadCancelToken.token,
        });

        const b = artifactFileResponse.data;
        // A Blob() is almost a File() - it's just missing the two properties below which we will add
        b.lastModifiedDate = release.date;
        b.name = artifact.url.split('/').pop();

        await this.uploadArtifact(b as File);

      } catch (error) {
        Notify.Error(this.logger, `The artifact was not downloaded from the server`, error);
        if (this.systemUpgradeStore.UpgradeState === UpgradeState.Downloading) {
          this.systemUpgradeStore.setState(UpgradeState.DownloadFailed);
        }
      }

    } catch (error) {
      Notify.Error(this.logger, `Error looking for an update artifact`, error);
      if (this.systemUpgradeStore.UpgradeState === UpgradeState.Downloading) {
        this.systemUpgradeStore.setState(UpgradeState.DownloadFailed);
      }
      return;
    }
  }

  private async cancelDownload() {
    if (this.networkingStore.Status === NetworkingStatus.ConnectedGlobal) {
      try {
        await this.systemUpgradeService.downloadCancel();
        this.systemUpgradeStore.setState(UpgradeState.Idle);
        this.downloadProgression = 0;
      } catch (error) {
        Notify.Error(this.logger, `Could not abort the update`, error);
      }
    } else {
      this.downloadCancelToken.cancel(`Download cancelled`);
      this.systemUpgradeStore.setState(UpgradeState.Idle);
    }
  }

  private async uploadArtifact(file: File | null) {

    if (Array.isArray(file)) {
      if (file.length > 0) {
        file = file[0];
      }
    }

    if (!file) {
      Notify.Error(this.logger, `Please select a file first`);
      return;
    }

    this.systemUpgradeStore.setState(UpgradeState.Uploading);

    this.logger.debug('Uploading artifact...', file);
    this.uploadProgression = 0;

    try {
      this.uploadCancelToken = Axios.CancelToken.source();

      await this.systemUpgradeService.upload(
        file,
        (progression: number) => {
          this.uploadProgression = progression;
        },
        this.uploadCancelToken.token,
        this.slowConnection ? this.uploadTimeoutSlow : this.uploadTimeout,
      );

      this.logger.debug('Artifact uploaded');
      this.systemUpgradeStore.setState(UpgradeState.Downloaded);
      this.uploadProgression = 100;

    } catch (error) {
      Notify.Error(this.logger, `The artifact was not uploaded`, error);
      if (this.systemUpgradeStore.UpgradeState === UpgradeState.Uploading) {
        this.systemUpgradeStore.setState(UpgradeState.UploadFailed);
      }
    }
  }

  private async cancelUpload() {
    this.systemUpgradeStore.setState(UpgradeState.Idle);
    this.uploadCancelToken.cancel(`Upload cancelled`);
  }

  private async installUpdate() {

    try {
      if (this.systemUpgradeStore.UpgradeState === UpgradeState.Downloaded) {
        this.systemUpgradeStore.setState(UpgradeState.Applying);

        await this.systemUpgradeService.apply();
      }

      await this.systemOsService.reboot('Update apply reboot');

      this.systemUpgradeStore.setState(UpgradeState.Stopping);

      const shutdownTimeout = new Date();
      shutdownTimeout.setSeconds((new Date()).getSeconds() + this.shutdownTimeout);

      this.updateTimer = 0;
      this.updateTimerInterval = setInterval(async () => {
        this.updateTimer++;
      }, 1000);

      // Check if we are still online
      while (await this.apiService.check(this.shutdownCheckTimeout * 1000)) {

        if (new Date() > shutdownTimeout) {
          Notify.Error(this.logger, `The gateway could not be restarted`, null, ['Please try again or restart manually']);
          try {
            await this.systemUpgradeStore.fetchState();
          } catch {

          }
          return;
        }

        // Wait before retrying
        await Time.sleep(this.shutdownCheckPeriod * 1000);
      }

      this.systemUpgradeStore.setState(UpgradeState.Starting);

      const startTimeout = new Date();
      startTimeout.setSeconds((new Date()).getSeconds() + this.rebootTimeout);

      // Check if we are online again
      while (!await this.apiService.check(this.rebootCheckTimeout * 1000)) {

        if (new Date() > startTimeout) {
          Notify.Error(this.logger, `The gateway does not respond`);
          this.systemUpgradeStore.setState(UpgradeState.RebootTimedOut);
          return;
        }

        // Wait before retrying
        await Time.sleep(this.rebootCheckPeriod * 1000);
      }

      clearInterval(this.updateTimerInterval);

      Notify.Success(this.logger, `The gateway has restarted`);

      this.systemUpgradeStore.setState(UpgradeState.Checking);
      this.checksRefreshInterval = setInterval(async () => {
        await this.performChecks();
      }, this.checksRefreshPeriod * 1000);

    } catch (error) {
      Notify.Error(this.logger, `Could not update`, error);
      this.systemUpgradeStore.setState(UpgradeState.ApplyFailed);
    }
  }

  private async cancelUpdate() {

    if (this.systemUpgradeStore.UpgradeState === UpgradeState.Downloaded) {
      try {
        this.systemUpgradeStore.setState(UpgradeState.Idle);
        await this.systemUpgradeService.cancelDownloaded();
      } catch (error) {
        Notify.Error(this.logger, `Could not remove the downloaded image`, error);
      }
    } else {

      try {
        await this.systemUpgradeService.cancel();
        this.systemUpgradeStore.setState(UpgradeState.Idle);
      } catch (error) {
        Notify.Error(this.logger, `Could not cancel the installed update`, error);
      }
    }

  }

  private async performChecks() {
    this.checksStatusRefreshing = true;

    try {
      await this.getNetworkStatus();
      await this.getForwarderStatus();
    } finally {
      this.checksStatusRefreshing = false;
    }
  }

  private async getForwarderStatus() {
    try {
      await this.loraForwardersStore.fetchActiveForwarder();
      await this.loraForwardersStore.fetchActiveForwarderState();
    } catch (error) {
      Notify.Error(this.logger, `Could not check LoRa forwarder status`, error);
    }
  }

  private async persistUpdate() {
    this.systemUpgradeStore.setState(UpgradeState.Committing);
    try {
      await this.systemUpgradeService.persist();
      this.systemUpgradeStore.setState(UpgradeState.Completed);
    } catch (error) {
      Notify.Error(this.logger, `Could not commit the installed update`, error);
      this.systemUpgradeStore.setState(UpgradeState.CommitFailed);
    }
  }

  private async rollbackUpdate() {
    this.systemUpgradeStore.setState(UpgradeState.RollingBack);
    try {
      await this.systemUpgradeService.rollback();

      await this.systemOsService.reboot('Update rollback reboot');
      const msg = `The system is now restarting to rollback the update`;
      Notify.Info(this.logger, msg);

      this.systemUpgradeStore.setState(UpgradeState.Idle);
    } catch (error) {
      Notify.Error(this.logger, `Could not rollback the update`, error);
    }
  }

}
